/**
 * BotOpponentPicker (#499) — champion + rating-tier picker for the practice
 * mode setup screen's bot opponent seat, sourced from a live `listBots`
 * reply (the client-safe, promoted/1v1-eligible roster — see
 * `WIRE_BOT_ENTRY_SCHEMA`). A single flat `<select>` keyed by `manifestId`
 * (not a champion→tier cascade): with today's tiny ladder this is simpler
 * and just as usable, and — critically — the VALUE this picker produces
 * IS the pin a saveable/replayable practice config stores (`manifestId`),
 * never a re-derived champion+tier pair.
 */

import type { ReactElement } from "react";
import type { WireBotEntry } from "@pit/protocol";
import { CHAMPIONS } from "../champions/catalog.js";

function championName(id: string): string {
  return CHAMPIONS.find((c) => c.id === id)?.name ?? id;
}

export interface BotOpponentPickerProps {
  readonly bots: readonly WireBotEntry[];
  /** The selected entry's `manifestId`, or `null` if none is selected yet. */
  readonly value: string | null;
  readonly onChange: (manifestId: string) => void;
  readonly disabled?: boolean;
  /** #676: `data-testid` prefix — lets a screen render several independent
   *  instances (e.g. one per 3v3 seat) with distinct, stable test ids.
   *  Defaults to `"bot-opponent"`, preserving every pre-#676 call site's
   *  existing ids byte-for-byte. */
  readonly testIdPrefix?: string;
}

export function BotOpponentPicker({
  bots,
  value,
  onChange,
  disabled = false,
  testIdPrefix = "bot-opponent",
}: BotOpponentPickerProps): ReactElement {
  if (bots.length === 0) {
    return (
      <div data-testid={`${testIdPrefix}-picker-empty`} style={{ opacity: 0.7, fontSize: 13 }}>
        No promoted bot checkpoints available yet.
      </div>
    );
  }
  const sorted = [...bots].sort((a, b) => {
    const nameA = championName(a.champion);
    const nameB = championName(b.champion);
    if (nameA !== nameB) return nameA < nameB ? -1 : 1;
    return a.ratingTier - b.ratingTier;
  });
  return (
    <label
      data-testid={`${testIdPrefix}-picker`}
      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}
    >
      <span style={{ opacity: 0.75 }}>Bot opponent</span>
      <select
        data-testid={`${testIdPrefix}-select`}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        style={{
          padding: "6px 10px",
          fontSize: 14,
          borderRadius: 4,
          border: "1px solid #3a4757",
          background: "#161c24",
          color: "#dfe6ef",
        }}
      >
        <option value="" disabled>
          Select a bot…
        </option>
        {sorted.map((b) => (
          <option key={b.manifestId} value={b.manifestId}>
            {championName(b.champion)} — Tier {String(b.ratingTier)}
          </option>
        ))}
      </select>
    </label>
  );
}
